import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useOutbox, useOnline, applyOutbox } from "@/lib/offline";
import { useAuth } from "@/context/AuthContext";
import { addressLine, isoDate, fmtDate, SLOTS } from "@/lib/format";
import { StatusBadge, PriorityBadge, TypeBadge, SlaChip } from "@/components/StatusBadge";

const EMPTY_IMG = "https://images.unsplash.com/photo-1576607699513-99a933f942f6?crop=entropy&cs=srgb&fm=jpg&q=80&w=900";

const Card = ({ o }) => (
  <Link to={`/tecnico/ordine/${o.id}`} data-testid={`tech-order-card-${o.external_order_id}`}
    className={`block border bg-white p-4 transition-transform active:scale-[0.99] border-l-4 ${o.service_type === "ASSURANCE" ? "border-l-urgent" : "border-l-primary"}`}>
    <div className="flex items-center justify-between">
      <TypeBadge type={o.service_type} />
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-testid={`tech-card-time-${o.external_order_id}`}>{SLOTS[o.slot]}{o.delivery?.appointment_time && ` · ore ${o.delivery.appointment_time}`}</span>
    </div>
    <div className="mt-2 flex items-center justify-between">
      <span className="font-mono text-xl font-semibold">{o.external_order_id}</span>
      <ChevronRight className="h-5 w-5 text-muted-foreground" />
    </div>
    <div className="mt-1 text-sm font-medium">{o.customer_name}</div>
    <div className="mt-1 flex items-start gap-1 text-sm text-muted-foreground"><MapPin className="mt-0.5 h-4 w-4 shrink-0" />{addressLine(o.address)}</div>
    <div className="mt-3 flex flex-wrap gap-1.5"><StatusBadge status={o.status} />{o.pending && <span className="bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold uppercase text-amber-900" data-testid={`tech-card-pending-${o.external_order_id}`}>Da sincronizzare</span>}{o.priority !== "ORDINARIA" && <PriorityBadge priority={o.priority} />}<SlaChip order={o} /></div>
  </Link>
);

const Group = ({ title, items, testId }) => items.length > 0 && (
  <section className="mb-8" data-testid={testId}>
    <div className="mb-3 flex items-baseline justify-between"><h2 className="font-display text-lg font-bold">{title}</h2><span className="font-mono text-sm text-muted-foreground">{items.length}</span></div>
    <div className="space-y-3">{items.map((o) => <Card key={o.id} o={o} />)}</div>
  </section>
);

export default function TechHome() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [offline, setOffline] = useState(false);
  const outbox = useOutbox();
  const online = useOnline();
  useEffect(() => {
    api.get("/tech/orders").then((r) => {
      setOrders(r.data);
      setOffline(!!r.offline);
      if (!r.offline) r.data.forEach((o) => api.get(`/tech/orders/${o.id}`).catch(() => {}));
    }).catch(() => setOrders([]));
  }, [outbox.length, online]);
  const today = isoDate(new Date());
  const list = (orders || []).map((o) => applyOutbox(o, outbox));
  const active = list.filter((o) => o.status === "IN_PROGRESS");
  const todays = list.filter((o) => o.status === "PLANNED" && o.scheduled_date <= today);
  const next = list.filter((o) => o.status === "PLANNED" && o.scheduled_date > today);
  const done = list.filter((o) => ["COMPLETED", "TO_VALIDATE", "VALIDATED", "SUSPENDED"].includes(o.status));

  return (
    <div data-testid="tech-home">
      <div className="mb-6 fade-up">
        <div className="micro-label">{fmtDate(today)}</div>
        <h1 className="mt-1 font-display text-3xl font-extrabold">Ciao {user.name.split(" ")[0]}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{todays.length + active.length} {todays.length + active.length === 1 ? "intervento" : "interventi"} da gestire oggi</p>
        {offline && <p className="mt-2 border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs" data-testid="tech-offline-data">Dati salvati sul dispositivo: verranno aggiornati al ritorno della connessione.</p>}
      </div>
      {orders && list.length === 0 && (
        <div className="overflow-hidden border bg-white" data-testid="tech-empty">
          <img src={EMPTY_IMG} alt="" className="h-48 w-full object-cover" />
          <div className="p-5 text-sm text-muted-foreground">Nessun intervento assegnato. I nuovi incarichi appariranno qui appena pianificati.</div>
        </div>
      )}
      <Group title="In lavorazione" items={active} testId="tech-group-active" />
      <Group title="Oggi" items={todays} testId="tech-group-today" />
      <Group title="Prossimi giorni" items={next} testId="tech-group-next" />
      <Group title="Chiusi oggi" items={done} testId="tech-group-done" />
    </div>
  );
}
